/**
 * The one progress bar (TMMAPP-0156 item 4; visual contract v2 §6, prototype states 30 and 43): a
 * filled track under the fill, never an outlined bar. Account cards (the allocated share, a loan
 * paid), goals and envelopes all draw it.
 *
 * - `tone` picks the fill: `progress` paints `brandBoundary` (the non-text brand), `over` a budget
 *   past its target in `moneyNegative`, `reached` a goal met in `successFg` (TMMAPP-0154 item 13),
 *   `loan` a loan paid in `successFg` too: the prototype's lighter sage on its `still` cards
 *   (state 30), as the guaranteed 3:1 sage on the track (TMMAPP-0157 item 5).
 * - With a `label` it's a named `progressbar` (0–100, rounded); without one it's decorative
 *   (`aria-hidden`), where the words beside it already say the share.
 * - The track is `hover`, the prototype's `#efe8db` (visual contract v2 §0). Each fill on it is an
 *   audited pair, `<fill> / <canvas> under hover` in `tests/contrast.ts`, with no exemption.
 *
 * Working indicators (an upload, a file being read, the entry gate) stay MUI's `LinearProgress`.
 */
import { Box, useTheme } from '@mui/material';
import { resolveTokens } from '@/theme/theme';

export type ProgressTone = 'progress' | 'over' | 'reached' | 'loan';

interface ProgressTrackProps {
  /** The filled share, 0–100 (clamped). */
  value: number;
  tone?: ProgressTone | undefined;
  /** The track's height in px (6 on cards and goals). */
  height?: number;
  /** The bar's accessible name; absent, the bar is decorative. */
  label?: string | undefined;
}

export function ProgressTrack({ value, tone = 'progress', height = 6, label }: ProgressTrackProps) {
  const tokens = resolveTokens(useTheme());
  const share = Math.min(100, Math.max(0, Number.isFinite(value) ? value : 0));
  const fill =
    tone === 'over'
      ? tokens.moneyNegative
      : tone === 'reached' || tone === 'loan'
        ? tokens.successFg
        : tokens.brandBoundary;
  const named =
    label === undefined
      ? { 'aria-hidden': true }
      : {
          role: 'progressbar',
          'aria-label': label,
          'aria-valuemin': 0,
          'aria-valuemax': 100,
          'aria-valuenow': Math.round(share),
        };
  return (
    <Box
      {...named}
      data-progress-track=""
      sx={{
        height,
        borderRadius: `${height / 2}px`,
        bgcolor: tokens.hover,
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      <Box
        data-progress-state={tone}
        sx={{ height: '100%', width: `${share}%`, bgcolor: fill, borderRadius: 'inherit' }}
      />
    </Box>
  );
}
